import { Routes } from '@/config/routes';
import {
  clearAuthCookies,
  persistQualidocAuthCookies,
} from '@/lib/auth/auth-cookies';
import { isQualidocJwt } from '@/lib/auth/qualidoc-jwt';
import { AUTH_REFRESH_TOKEN_KEY, AUTH_TOKEN_KEY } from '@/lib/constants';
import type { SearchParamOptions } from '@/types';
import axios, { AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import Router from 'next/router';
import { getDefaultStore } from 'jotai';
import { authorizationAtom } from '@/store/authorization-atom';
import { safeClearWebStorage } from '@/lib/safe-storage';
import { SESSION_CLEAR_PRESERVED_KEYS } from '@/lib/attribution-storage-keys';

const defaultAxiosConfig = {
  baseURL: process.env.NEXT_PUBLIC_REST_API_ENDPOINT,
  timeout: 5000000,
  headers: {
    'Content-Type': 'application/json',
  },
};

const store = getDefaultStore();

const noInterceptorAxios = axios.create();
const Axios = axios.create({
  ...defaultAxiosConfig,
});

/** Avoid duplicate refresh/logout storms when many requests fail with 401 at once. */
let refreshInFlight: Promise<RefreshResult> | null = null;
let logoutInFlight = false;

const REFRESH_LOCK_KEY = 'qualidoc_auth_refresh_lock';
const REFRESH_LOCK_TTL_MS = 12_000;
const REFRESH_WAIT_MS = 12_000;
const REFRESH_POLL_MS = 150;
const REFRESH_RETRY_DELAY_MS = 400;

type RefreshResult = 'success' | 'auth_failed' | 'transient';

export class SessionExpiredError extends Error {
  readonly silent = true;

  constructor(message = 'Sessão expirada') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function readRefreshLock(): { id: string; until: number } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(REFRESH_LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string; until?: number };
    if (!parsed?.id || typeof parsed.until !== 'number') return null;
    if (parsed.until < Date.now()) {
      window.localStorage.removeItem(REFRESH_LOCK_KEY);
      return null;
    }
    return { id: parsed.id, until: parsed.until };
  } catch {
    return null;
  }
}

function tryAcquireRefreshLock(ownerId: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    if (readRefreshLock()) return false;
    window.localStorage.setItem(
      REFRESH_LOCK_KEY,
      JSON.stringify({ id: ownerId, until: Date.now() + REFRESH_LOCK_TTL_MS }),
    );
    return readRefreshLock()?.id === ownerId;
  } catch {
    return true;
  }
}

function releaseRefreshLock(ownerId: string) {
  if (typeof window === 'undefined') return;
  try {
    if (readRefreshLock()?.id === ownerId) {
      window.localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

/**
 * Cross-tab lock so only one tab hits /auth/refresh; others wait for shared cookies.
 */
async function withRefreshLock<T>(
  refreshTokenBefore: string | undefined,
  run: () => Promise<T>,
  onPeerSuccess: () => T,
): Promise<T> {
  const ownerId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const deadline = Date.now() + REFRESH_WAIT_MS;

  while (Date.now() < deadline) {
    const currentRefresh = Cookies.get(AUTH_REFRESH_TOKEN_KEY);
    if (
      currentRefresh &&
      currentRefresh !== refreshTokenBefore &&
      isQualidocJwt(currentRefresh) &&
      isQualidocJwt(Cookies.get(AUTH_TOKEN_KEY))
    ) {
      return onPeerSuccess();
    }

    if (tryAcquireRefreshLock(ownerId)) {
      try {
        const again = Cookies.get(AUTH_REFRESH_TOKEN_KEY);
        if (
          again &&
          again !== refreshTokenBefore &&
          isQualidocJwt(again) &&
          isQualidocJwt(Cookies.get(AUTH_TOKEN_KEY))
        ) {
          return onPeerSuccess();
        }
        return await run();
      } finally {
        releaseRefreshLock(ownerId);
      }
    }

    await sleep(REFRESH_POLL_MS);
  }

  // Lock wait timed out — attempt refresh ourselves to avoid leaving the user stuck.
  return run();
}

// Change request data/error here
Axios.interceptors.request.use((config) => {
  const token = Cookies.get(AUTH_TOKEN_KEY);

  if (token) {
    //@ts-ignore
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token ? token : ''}`,
    };
  }
  return config;
});

Axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    const isLoginEndpoint = error.config?.url?.includes('login');
    const isLogoutEndpoint = error.config?.url?.includes('logout');
    const isRefreshEndpoint = error.config?.url?.includes('auth/refresh');

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !isLoginEndpoint &&
      !isLogoutEndpoint &&
      !isRefreshEndpoint
    ) {
      const errorCode = error.response?.data?.error?.code;
      if (
        errorCode === 'ACCOUNT_BLOCKED' ||
        errorCode === 'SESSION_REVOKED'
      ) {
        handleLogout();
        return Promise.reject(new SessionExpiredError());
      }

      originalRequest._retry = true;

      const refreshed = await tryRefreshToken();

      if (refreshed === 'success') {
        const newToken = Cookies.get(AUTH_TOKEN_KEY);
        if (newToken && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        }
        return Axios(originalRequest);
      }

      // Network / 5xx on refresh: keep cookies; surface the original API error.
      if (refreshed === 'transient') {
        return Promise.reject(toSerializableAxiosError(error));
      }

      handleLogout();
      return Promise.reject(new SessionExpiredError());
    }

    // Axios errors embed request/socket Agents — circular and not JSON-serializable.
    // Next.js jest-worker crashes when trying to serialize them across processes.
    return Promise.reject(toSerializableAxiosError(error));
  },
);

function toSerializableAxiosError(error: any): Error {
  if (error instanceof SessionExpiredError) {
    return error;
  }

  const status = error?.response?.status;
  const data = error?.response?.data;
  const code = error?.code;
  const url = error?.config?.url;
  const baseMessage =
    (typeof data?.message === 'string' && data.message) ||
    (typeof error?.message === 'string' && error.message) ||
    'Request failed';

  const message =
    status != null
      ? `${baseMessage} (${status}${url ? ` ${url}` : ''})`
      : `${baseMessage}${code ? ` [${code}]` : ''}${url ? ` ${url}` : ''}`;

  const safe = new Error(message);
  safe.name = error?.name || 'AxiosError';
  (safe as any).status = status;
  (safe as any).code = code;
  (safe as any).response = data != null ? { status, data } : undefined;
  return safe;
}

function classifyRefreshFailure(error: any): RefreshResult {
  const status = error?.response?.status;
  if (status == null) {
    return 'transient';
  }
  if (status >= 500) {
    return 'transient';
  }
  return 'auth_failed';
}

async function performRefreshRequest(
  refreshToken: string,
): Promise<RefreshResult> {
  let lastFailure: RefreshResult = 'auth_failed';

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const response = await noInterceptorAxios.post(
        `${process.env.NEXT_PUBLIC_REST_API_ENDPOINT}/auth/refresh`,
        {},
        {
          headers: {
            'x-refresh-token': refreshToken,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        },
      );

      const { token, refresh_token, permissions } = response.data;

      const persisted = persistQualidocAuthCookies({
        accessToken: token,
        refreshToken: refresh_token,
        permissions,
      });
      if (!persisted) {
        return 'auth_failed';
      }

      store.set(authorizationAtom, true);
      return 'success';
    } catch (error: any) {
      lastFailure = classifyRefreshFailure(error);
      if (lastFailure === 'transient' && attempt === 0) {
        await sleep(REFRESH_RETRY_DELAY_MS);
        continue;
      }
      return lastFailure;
    }
  }

  return lastFailure;
}

async function tryRefreshToken(): Promise<RefreshResult> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const refreshTokenBefore = Cookies.get(AUTH_REFRESH_TOKEN_KEY);
      if (!isQualidocJwt(refreshTokenBefore)) return 'auth_failed';

      return await withRefreshLock(
        refreshTokenBefore,
        async () => {
          const tokenNow = Cookies.get(AUTH_REFRESH_TOKEN_KEY);
          if (!isQualidocJwt(tokenNow)) return 'auth_failed';
          return performRefreshRequest(tokenNow!);
        },
        () => {
          store.set(authorizationAtom, true);
          return 'success' as const;
        },
      );
    } catch {
      return 'transient';
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

function handleLogout() {
  if (logoutInFlight) {
    return;
  }
  logoutInFlight = true;

  try {
    clearAuthCookies();
    safeClearWebStorage({ preserveLocalKeys: SESSION_CLEAR_PRESERVED_KEYS });
    store.set(authorizationAtom, false);

    if (Router.pathname !== Routes.logout) {
      void Router.replace(Routes.logout).finally(() => {
        logoutInFlight = false;
      });
      return;
    }
  } catch {
    // fall through to reset the dedupe flag
  }

  logoutInFlight = false;
}
export class HttpClientWithoutInterceptors {
  static async get<T>(url: string, params?: unknown) {
    const response = await Axios.get<T>(url, { params });
    return response.data;
  }

  static async post<T>(url: string, data: unknown, options?: any) {
    const response = await Axios.post<T>(url, data, options);
    return response.data;
  }
}

export class HttpClient {
  static async get<T>(
    url: string,
    params?: unknown,
    config?: Omit<AxiosRequestConfig, 'params'>,
  ) {
    const response = await Axios.get<T>(url, { ...config, params });
    return response.data;
  }

  static async getResponse<T>(
    url: string,
    params?: unknown,
    config?: Omit<AxiosRequestConfig, 'params'>,
  ) {
    return Axios.get<T>(url, { ...config, params });
  }

  static async getBlobResponse(url: string, config?: AxiosRequestConfig) {
    return Axios.get<Blob>(url, {
      ...config,
      responseType: 'blob',
    });
  }

  static async post<T>(url: string, data: unknown, options?: any) {
    const response = await Axios.post<T>(url, data, options);
    return response.data;
  }

  static async put<T>(url: string, data: unknown) {
    const response = await Axios.put<T>(url, data);
    return response.data;
  }

  static async patch<T>(url: string, data: unknown) {
    const response = await Axios.patch<T>(url, data);
    return response.data;
  }

  static async delete<T>(url: string) {
    const response = await Axios.delete<T>(url);
    return response.data;
  }

  static formatSearchParams(params: Partial<SearchParamOptions>) {
    return Object.entries(params)
      .filter(([, value]) => Boolean(value))
      .map(([k, v]) =>
        [
          'type',
          'categories',
          'tags',
          'author',
          'manufacturer',
          'shops',
        ].includes(k)
          ? `${k}.slug:${v}`
          : `${k}:${v}`,
      )
      .join(';');
  }
}
